import React, {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useState,
  useEffect,
  useMemo,
} from "react";
import { useDispatch, useSelector } from "react-redux";

import { RootState } from "src/redux/reducers";
import { showErrorToast } from "src/utils/methods";
import { tableDataType } from "src/app/components/Table/Table";
import { ChangeWillAffectManyRowsModal } from "src/app/modals/ChangeWillAffectManyRowsModal/ChangeWillAffectManyRowsModal";
import { addPendingTaskMetaFieldFiles } from "src/redux";
import {
  ALLOWED_TYPES_FOR_ATTACHMENT_UPLOAD,
  MAX_SIZE_SINGLE_ATTACHMENT,
} from "src/utils/uploadFunctionsUtils";
import { showToast } from "../../methods/showToast";
import FileField from "./FileField";
import { deleteProjectFile, uploadProjectFile } from "./projectFunctions";
import { deleteTaskFile, uploadTaskFile } from "./taskFunctions";
import { addNewFilesFromFileList } from "./utils";
import { MetaFileData } from "../MultipleFileField/MultipleFileField";

const getInvalidFilesMessage = (files: File[]): string | null => {
  const hasInvalidType = files.some((file) => {
    const extension = file.name.split(".").pop()?.toLowerCase() ?? "";
    return !ALLOWED_TYPES_FOR_ATTACHMENT_UPLOAD.includes(extension);
  });
  if (hasInvalidType) {
    return "Invalid file type.";
  }

  const hasOversizedFile = files.some(
    (file) => file.size / (1000 * 1000) > MAX_SIZE_SINGLE_ATTACHMENT,
  );
  if (hasOversizedFile) {
    return "File is too large.";
  }

  return null;
};

interface SelectedTaskForFileField {
  id: string;
  taskId: string;
  metadata?: { uuid?: string; wsGlobalTaskMetaFieldUuid?: string }[];
}

interface TableFileFieldProps {
  uuid: string;
  dataType: tableDataType;
  objectId: string; // objectId is projectId for dataType Project
  files: MetaFileData[];
  projectId?: string;
  readOnly?: boolean;
  selectedTasks?: SelectedTaskForFileField[];
  rowId?: string;
  fieldName?: string;
}

export default function TableFileField({
  uuid,
  dataType,
  objectId,
  files,
  projectId,
  readOnly,
  selectedTasks,
  rowId,
  fieldName,
}: TableFileFieldProps) {
  const [filesData, setFilesData] = useState<MetaFileData[]>([]);
  const [loading, setLoading] = useState(false);
  const [pendingUploadEvent, setPendingUploadEvent] =
    useState<ChangeEvent<HTMLInputElement> | null>(null);

  const {
    mainReducer: { activeWorkspaceUuid },
  } = useSelector((state: RootState) => state);

  const dispatch = useDispatch();

  // Some selected tasks may not have this field at all (e.g. a global
  // field that was never added to that task's campaign). Other field
  // types currently reject the whole bulk update with a single error
  // when that happens (backend aborts the batch on the first task
  // missing the field) rather than silently skipping it — match that
  // behaviour here for consistency until the team decides otherwise.
  const hasTaskMissingField = useMemo(
    () =>
      (selectedTasks || []).some(
        (task) =>
          !(task.metadata || []).some(
            (field) =>
              field.uuid === uuid || field.wsGlobalTaskMetaFieldUuid === uuid,
          ),
      ),
    [selectedTasks, uuid],
  );

  const uploadToSingleTask = async (
    e: ChangeEvent<HTMLInputElement>,
    taskId: string,
    onLocalFilesChange: Dispatch<SetStateAction<MetaFileData[]>>,
  ) => {
    if (!projectId) {
      throw new Error("Project id is required");
    }
    await uploadTaskFile(
      e,
      dispatch,
      uuid,
      activeWorkspaceUuid,
      onLocalFilesChange,
      projectId,
      taskId,
    );
  };

  const uploadToMultipleTasks = async (e: ChangeEvent<HTMLInputElement>) => {
    const target = e.target as HTMLInputElement;
    if (!target.files?.length) {
      return;
    }

    const invalidFilesMessage = getInvalidFilesMessage(
      Array.from(target.files),
    );
    if (invalidFilesMessage) {
      // Validate once up front so a bad file shows a single toast instead
      // of one per selected task (each task would otherwise run its own
      // validation independently inside uploadTaskFile).
      showToast("error", "Error", invalidFilesMessage);
      return;
    }

    if (hasTaskMissingField) {
      // Match the current behaviour of other field types: if any selected
      // row doesn't have this field, reject the whole bulk update with a
      // single error instead of applying it to some rows only.
      showToast(
        "error",
        "Error",
        "This field doesn't exist for one or more selected rows.",
      );
      return;
    }

    setLoading(true);
    try {
      const taskIds = (selectedTasks || []).map((task) => task.taskId);

      // Show every selected row's uploading placeholder immediately,
      // instead of waiting for each row's own upload to finish.
      dispatch(
        addPendingTaskMetaFieldFiles(
          taskIds,
          uuid,
          addNewFilesFromFileList(target.files),
        ),
      );

      for (const task of selectedTasks || []) {
        // Every row (including this one) now reads its files from redux,
        // kept in sync by the pending-files dispatch above and by the
        // per-task refresh that uploadTaskFile triggers once it finishes.
        // eslint-disable-next-line no-await-in-loop
        await uploadToSingleTask(e, task.taskId, () => {});
      }
    } catch (error) {
      showErrorToast();
    } finally {
      setLoading(false);
    }
  };

  const uploadFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const isPartOfMultiSelection =
      dataType === tableDataType.Task &&
      selectedTasks &&
      selectedTasks.length > 1 &&
      selectedTasks.find((task) => task.id === rowId);

    if (isPartOfMultiSelection) {
      setPendingUploadEvent(e);
      return;
    }

    setLoading(true);
    try {
      switch (dataType) {
        case tableDataType.Project:
          await uploadProjectFile(
            e,
            dispatch,
            uuid,
            objectId,
            activeWorkspaceUuid,
            setFilesData,
          );
          break;

        case tableDataType.Task:
          await uploadToSingleTask(e, objectId, setFilesData);
          break;
        default:
          break;
      }
    } catch (error) {
      showErrorToast();
    } finally {
      setLoading(false);
    }
  };

  const confirmMultiUpload = () => {
    const event = pendingUploadEvent;
    setPendingUploadEvent(null);
    if (event) {
      uploadToMultipleTasks(event);
    }
  };

  const cancelMultiUpload = () => {
    setPendingUploadEvent(null);
  };

  const deleteFile = async (assetId: string) => {
    switch (dataType) {
      case tableDataType.Project:
        deleteProjectFile(
          assetId,
          uuid,
          dispatch,
          objectId,
          setFilesData,
          files || [],
        );
        break;

      case tableDataType.Task:
        deleteTaskFile(
          assetId,
          uuid,
          setFilesData,
          files || [],
          objectId,
          dispatch,
        );
        break;
      default:
        console.error("Unknown data type.");
        break;
    }
  };

  useEffect(() => {
    setFilesData(files || []);
  }, [files]);

  return (
    <>
      <FileField
        handleUploadFile={uploadFile}
        handleDeleteFile={deleteFile}
        isLoading={loading || filesData.some((file) => file.loading)}
        filesData={filesData}
        readOnly={readOnly}
      />

      {pendingUploadEvent && (
        <ChangeWillAffectManyRowsModal
          onClose={cancelMultiUpload}
          fieldName={fieldName || ""}
          rowsNumber={selectedTasks?.length || 0}
          onConfirmClick={confirmMultiUpload}
          onCancelClick={cancelMultiUpload}
        />
      )}
    </>
  );
}
